import { AnimatePresence, motion } from 'motion/react';
import { Archive, Plus } from 'lucide-react';
import { staggerItem } from '@/lib/motion';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { SidebarSection } from '@reflex/ui/components/sidebar/SidebarSection';
import { ActiveAgentRow } from '../ActiveAgentRow';
import { DraftAgentRow } from '../DraftAgentRow';
import type { Agent, AgentActivity, AgentDraft } from '@/lib/types';
import type { AgentTimeBucket, SidebarSectionContext, SidebarSessionItem } from '@/lib/agent-utils';

interface AgentSidebarSessionGroupProps {
  /** Section identity: a time bucket in the time views, a group id in the group view. */
  groupKey: string;
  label: string;
  items: SidebarSessionItem[];
  collapsed: boolean;
  selectedAgentId: string | null;
  selectedDraftId: string | null;
  activities: Map<string, AgentActivity>;
  onToggle: (key: string) => void;
  onSelectAgent: (agent: Agent) => void;
  onSelectDraft: (draft: AgentDraft) => void;
  onArchiveAgent: (agent: Agent) => void;
  onDeleteAgent: (agent: Agent) => void;
  onTogglePin: (agent: Agent) => void;
  onDeleteDraft: (draft: AgentDraft) => void;
  /**
   * Archive everything in the section. Only meaningful for a time bucket,
   * whose calendar bounds the server can sweep; omitted in the group view,
   * where the section header offers nothing in its place.
   */
  onArchiveAll?: (bucket: AgentTimeBucket, agents: Agent[], label: string) => void;
  /** What this section stands for, so its rows can offer the matching removal. */
  sectionContext?: SidebarSectionContext | null;
  /**
   * Start a run already filed into this section's group. Only offered on group
   * sections: a label says what kind of work a run is, which is the
   * classifier's call to make once the prompt exists, not something you pick a
   * section to declare. Time buckets have nothing to launch into at all.
   */
  onLaunchInGroup?: (group: SidebarSectionContext) => void;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (agent: Agent, shiftKey: boolean) => void;
}

export function AgentSidebarSessionGroup({
  groupKey,
  label,
  items,
  collapsed,
  selectedAgentId,
  selectedDraftId,
  activities,
  onToggle,
  onSelectAgent,
  onSelectDraft,
  onArchiveAgent,
  onDeleteAgent,
  onTogglePin,
  onDeleteDraft,
  onArchiveAll,
  sectionContext,
  onLaunchInGroup,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}: AgentSidebarSessionGroupProps) {
  const agents = items
    .filter((item): item is Extract<SidebarSessionItem, { kind: 'agent' }> => item.kind === 'agent')
    .map((item) => item.agent);

  // The "Ungrouped" bucket carries no `sectionContext`, so it gets no "+" —
  // there is no group to launch into, and the sidebar's own "New agent" button
  // already covers "start one that belongs nowhere yet".
  const launchGroup =
    onLaunchInGroup && sectionContext?.kind === 'group' && !selectionMode ? sectionContext : null;

  return (
    <SidebarSection
      label={label}
      open={!collapsed}
      onOpenChange={() => onToggle(groupKey)}
      data-testid={`agent-sidebar-section-${groupKey}`}
      triggerTestId={`agent-sidebar-section-toggle-${groupKey}`}
      actions={
        launchGroup ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`New agent in ${launchGroup.name}`}
                data-testid={`agent-sidebar-section-launch-${groupKey}`}
                className="shrink-0 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/section:opacity-100"
                onClick={() => onLaunchInGroup?.(launchGroup)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">New agent in this group</TooltipContent>
          </Tooltip>
        ) : onArchiveAll && agents.length > 0 && !selectionMode ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Archive all ${label}`}
                data-testid={`agent-sidebar-section-archive-all-${groupKey}`}
                className="shrink-0 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/section:opacity-100"
                onClick={() => onArchiveAll(groupKey as AgentTimeBucket, agents, label)}
              >
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Archive all</TooltipContent>
          </Tooltip>
        ) : undefined
      }
    >
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <motion.div key={item.id} {...staggerItem}>
            {item.kind === 'agent' ? (
              <ActiveAgentRow
                agent={item.agent}
                selected={selectedAgentId === item.agent.id}
                activity={activities.get(item.agent.id) ?? 'stopped'}
                onSelect={onSelectAgent}
                onArchive={onArchiveAgent}
                onDelete={onDeleteAgent}
                onTogglePin={onTogglePin}
                selectionMode={selectionMode}
                checked={selectedIds?.has(item.agent.id) ?? false}
                onToggleSelect={onToggleSelect}
                sectionContext={sectionContext}
              />
            ) : (
              <DraftAgentRow
                draft={item.draft}
                selected={selectedDraftId === item.draft.id}
                onSelect={onSelectDraft}
                onDelete={onDeleteDraft}
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </SidebarSection>
  );
}
